// Jun-2026 — Phase 4: PO–GRN–PI advisory panel inside the EXISTING PI form.
// Renders only when the invoice lines reference a Project Operations PO the
// user may see (backend 403/404 hides it). Advisory except PO quantity caps.
import { useEffect, useMemo, useState } from "react";
import { RefreshCw, GitCompareArrows, AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const CHIP = {
  "Matched": "bg-emerald-100 text-emerald-700",
  "GRN Ahead of PI": "bg-sky-100 text-sky-700",
  "PI Ahead of GRN": "bg-orange-100 text-orange-700",
  "Not Received": "bg-slate-100 text-slate-600",
  "Not Invoiced": "bg-slate-100 text-slate-600",
  "GRN Not Required": "bg-slate-100 text-slate-500",
  "Quantity Exception": "bg-red-100 text-red-700",
  "Rate Variance": "bg-amber-100 text-amber-700",
};

export const ProjectPIPanel = ({ lines, onSetReason }) => {
  const [panels, setPanels] = useState({});   // po_id -> {panel, match}
  const poIds = useMemo(() => [...new Set((lines || [])
    .map(l => (l.source_po_id || "").trim()).filter(Boolean))], [lines]);

  const load = () => {
    poIds.forEach(pid => {
      Promise.all([
        api.get(`/project-ops/pos/${pid}/pi-panel`),
        api.get(`/project-ops/pos/${pid}/three-way-match`),
      ]).then(([p, m]) => setPanels(s => ({ ...s, [pid]: { panel: p.data, match: m.data } })))
        .catch(() => setPanels(s => ({ ...s, [pid]: null })));
    });
  };
  useEffect(load, [poIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = poIds.map(pid => panels[pid]).filter(Boolean);
  if (visible.length === 0) return null;

  return (
    <div className="mt-4 space-y-4" data-testid="project-pi-panel">
      {visible.map(({ panel, match }) => {
        const proposedByUid = {};
        (lines || []).forEach((l, i) => {
          if ((l.source_po_id || "") !== panel.po_id) return;
          const row = (panel.lines || []).find(x => x.po_line_index === l.source_po_line_index);
          if (!row) return;
          let qty = Number(l.quantity || 0);
          // Aug-2026 — service/lumpsum (no-GRN) lines consume PO qty
          // proportionate to AMOUNT; mirror that in the advisory panel.
          if (!row.grn_required && Number(row.po_rate || 0) > 0) {
            const amt = Number(l.amount || 0) || qty * Number(l.rate || 0);
            if (amt > 0) qty = Math.round((amt / Number(row.po_rate)) * 10000) / 10000;
          }
          proposedByUid[row.po_line_uid] = (proposedByUid[row.po_line_uid] || 0) + qty;
        });
        return (
          <div key={panel.po_id} className="border border-orange-200 rounded bg-orange-50/30">
            <div className="px-4 py-2.5 border-b border-orange-200 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <GitCompareArrows size={14} className="text-[#F97316]" />
                <span className="text-xs font-bold" data-testid="panel-po-number">PO–GRN–PI · {panel.po_number}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#F97316] text-white uppercase tracking-wider" data-testid="project-procurement-badge">Project Procurement</span>
                <span className="text-[10px] text-slate-500">{panel.stage === "PO_APPROVED_PI_READY" ? "PI-Ready · GRN still receivable" : panel.stage}</span>
              </div>
              <div className="flex items-center gap-3">
                {match?.summary && (
                  <span className="text-[10px] text-slate-600" data-testid="match-summary">
                    Exceptions: <b>{match.summary.exception_count}</b>
                    {match.values_visible && <> · PO ₹{Number(match.summary.po_value || 0).toLocaleString("en-IN")} · GRN(prov) ₹{Number(match.summary.grn_value || 0).toLocaleString("en-IN")} · PI ₹{Number(match.summary.pi_value || 0).toLocaleString("en-IN")}</>}
                  </span>
                )}
                <a href="/projects" className="text-[10px] font-bold text-sky-700 hover:underline inline-flex items-center gap-1" data-testid="panel-project-link"><ExternalLink size={10} /> Project Workspace</a>
                <button type="button" onClick={load} className="text-slate-400 hover:text-slate-700" data-testid="panel-refresh"><RefreshCw size={12} /></button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid="panel-lines-table">
                <thead className="text-[9px] uppercase tracking-wider text-slate-500 border-b border-orange-200">
                  <tr>
                    <th className="text-left px-3 py-1.5">Item</th>
                    <th className="text-left px-3 py-1.5">Routing</th>
                    <th className="text-center px-3 py-1.5">GRN Req.</th>
                    <th className="text-right px-3 py-1.5">PO Qty</th>
                    <th className="text-right px-3 py-1.5">Accepted</th>
                    <th className="text-right px-3 py-1.5">Rej/Dmg</th>
                    <th className="text-right px-3 py-1.5">Prev. Invoiced</th>
                    <th className="text-right px-3 py-1.5">This PI</th>
                    <th className="text-right px-3 py-1.5">Remaining After</th>
                    <th className="text-left px-3 py-1.5">Receipt</th>
                    <th className="text-left px-3 py-1.5">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {(panel.lines || []).map(r => {
                    const proposed = proposedByUid[r.po_line_uid] || 0;
                    const m = (match?.lines || []).find(x => x.po_line_uid === r.po_line_uid);
                    const ahead = r.grn_required && (r.invoiced_qty + proposed) > (r.accepted_qty || 0) + 1e-6 && proposed > 0;
                    return (
                      <tr key={r.po_line_uid} className="border-b border-orange-100" data-testid={`panel-line-${r.po_line_index}`}>
                        <td className="px-3 py-1.5 font-semibold">{r.item_name}</td>
                        <td className="px-3 py-1.5"><span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">{r.routing_type}</span></td>
                        <td className="px-3 py-1.5 text-center">{r.grn_required ? "Yes" : <span className="text-slate-400">Not Required</span>}</td>
                        <td className="px-3 py-1.5 text-right font-bold">{r.po_qty}</td>
                        <td className="px-3 py-1.5 text-right">{r.grn_required ? r.accepted_qty : "—"}</td>
                        <td className="px-3 py-1.5 text-right text-slate-500">{r.grn_required ? `${r.rejected_qty}/${r.damaged_qty}` : "—"}</td>
                        <td className="px-3 py-1.5 text-right">{r.invoiced_qty}</td>
                        <td className="px-3 py-1.5 text-right font-bold text-[#F97316]">{proposed || ""}</td>
                        <td className="px-3 py-1.5 text-right">{Math.max(0, r.po_qty - r.invoiced_qty - proposed).toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-[10px]">{r.receipt_status || "—"}</td>
                        <td className="px-3 py-1.5">
                          {m && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${CHIP[m.match_status] || "bg-slate-100 text-slate-600"}`} data-testid={`match-status-${r.po_line_index}`}>{m.match_status}</span>}
                          {ahead && <AlertTriangle size={11} className="inline ml-1 text-red-600" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* PI-ahead warning + mandatory reason inputs */}
            {(lines || []).map((l, i) => {
              if ((l.source_po_id || "") !== panel.po_id) return null;
              const row = (panel.lines || []).find(x => x.po_line_index === l.source_po_line_index);
              if (!row || !row.grn_required) return null;
              const proposed = proposedByUid[row.po_line_uid] || 0;
              if ((row.invoiced_qty + proposed) <= (row.accepted_qty || 0) + 1e-6) return null;
              return (
                <div key={i} className="px-4 py-2.5 border-t border-red-200 bg-red-50 flex items-start gap-2" data-testid={`pi-ahead-warning-${i}`}>
                  <AlertTriangle size={13} className="text-red-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-[11px] font-bold text-red-700">
                      PI ahead of GRN — "{l.description}": cumulative PI qty {(row.invoiced_qty + proposed).toFixed(2)} exceeds accepted GRN qty {(row.accepted_qty || 0).toFixed(2)}. A mandatory reason is required; the invoice then continues through normal approval. Stock remains GRN-controlled.
                    </div>
                    <input value={l.pi_ahead_reason || ""} onChange={e => onSetReason(i, e.target.value)}
                      placeholder="Mandatory reason for invoicing ahead of receipt…"
                      data-testid={`pi-ahead-reason-${i}`}
                      className="mt-1.5 w-full px-2 py-1.5 text-[11px] border border-red-300 rounded focus:border-red-500 focus:outline-none bg-white" />
                  </div>
                </div>
              );
            })}
            <div className="px-4 py-1.5 border-t border-orange-200 text-[9px] text-slate-500">
              Advisory panel — only approved-PO quantity limits block. Stock for GRN-routed lines is managed exclusively via Project GRN; this PI will never create inventory for them. Provisional values use the approved PO rate (Option A — no revaluation).
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectPIPanel;
